/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { Injectable, OnDestroy } from '@angular/core';
import { ACDBidResponse, ACDRejectionEvent } from 'CalltakingCoreApi';
import { Subject } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import {
    acdAutoAnswer,
    acdBid,
    acdRejection
} from '../+state/user.actions';
import {
    selectAutoAnswerAssignedWonBid,
    selectNextBid
} from '../../call/+state/call.selectors';
import { filter, takeUntil } from 'rxjs/operators';
import { ClusterUrlProviderService } from '../../core/services/cluster-url-provider.service';
import { StompBaseConsumer } from '../../core/abstract/stomp-base-consumer';
import { StompManagerService } from '../../core/services/stomp-manager/stomp-manager.service';
import { Topic } from '../model/multiple-subscriptions';
import { selectIsPrimaryConnectionActive } from '../../call/+state/media.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
    providedIn: 'root'
})
export class AcdService extends StompBaseConsumer implements OnDestroy {
    private readonly API_BASE_URL = '/api/acd/v1/';

    private unsubscribe$ = new Subject<void>();

    constructor(
        private http: HttpClient,
        private clusterUrlProviderService: ClusterUrlProviderService,
        protected store: Store,
        protected stompManager: StompManagerService
    ) {
        super(stompManager, store);
            this.store.select(selectIsPrimaryConnectionActive)
                .pipe(takeUntilDestroyed())
                .subscribe((active) => {
                    if(active) {
                        this.acdActionsHandling();
                        return;
                    }
                    this.unsubscribe$.next();
                });
    }

    protected handleAcdRejectionMessage(event: ACDRejectionEvent) {
        console.warn(`ACD Assignment Revoked callId: ${event.callId}`);
        this.store.dispatch(acdRejection({ event }));
    }

    protected getTopics() {
        return [Topic.ACD_REVOKE];
    }

    protected getTopicHandlers(): { [p in Topic]?: (message: any) => void } {
        return {
            [Topic.ACD_REVOKE]: this.handleAcdRejectionMessage.bind(this)
        };
    }

    handleSubscriptionInit(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} ACD revoke topic ${topic} subscription success: ${receiptId}`);
    }

    handleSubscriptionEnd(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} ACD revoke ${topic} subscription end: ${receiptId}`);
    }

    handleStompError(topic: string, receiptId: string, error: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel ? clusterLabel : ''} ACD revoke topic ${topic} subscription failed=${error} for receiptId=${receiptId}`);
    }

    private acdActionsHandling() {
        this.store
            .pipe(selectNextBid)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((bid) => this.store.dispatch(acdBid({ bid: bid })));

        this.store
            .select(selectAutoAnswerAssignedWonBid)
            .pipe(
                filter((bid) => !!bid),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((autoAnswerBid) => this.store.dispatch(acdAutoAnswer({ bid: autoAnswerBid })));
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public bidForCall(callId: string, clusterName: string | undefined, bid: number, previouslyAssigned: boolean) {
        let params = new HttpParams().append('previouslyAssigned', previouslyAssigned);
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_BASE_URL}${callId}/bid/${bid}`);
        return this.http.post<ACDBidResponse>(url, {}, { params: params });
    }

    public acdAnswer(callId: string, clusterName: string | undefined, queueName: string | undefined) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_BASE_URL}${callId}/${queueName}/answer`);
        return this.http.post(url, {});
    }

    public acdReject(callId: string, clusterName: string | undefined) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, `${this.API_BASE_URL}${callId}/reject`);
        return this.http.post(url, {});
    }
}
